import React from 'react';
import { Link } from 'react-router-dom';
import {
  List,
  Show,
  useListContext,
  useShowContext,
  useRefresh,
  useNotify,
  useDataProvider,
  useGetList,
} from 'react-admin';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Calendar,
  User,
  DollarSign,
  Mail,
  Phone,
  Edit,
  Trash2,
  Check,
  Circle,
  X as XIcon,
} from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { Button } from '../components/ui/button';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { AvatarName, StatusBadge, SportTag } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider } from '../types/dataProvider';
import { RemoveFromClassAction, canRemoveFromClass } from '../components/admin/RemoveFromClassAction';

interface BookingListItem {
  id: number;
  // Backend BookingStatus. There is no "completed" booking status — asking the
  // API for it 422s.
  status: 'pending' | 'confirmed' | 'declined' | 'cancelled' | 'removed';
  created_at: string;
  user: { id: number; name: string; email: string; phone?: string };
  session: { id: number; session_type: string; title?: string; scheduled_at: string };
  trainer: { id: number; name: string };
  sport?: { id: number; name: string };
  price_paid?: number;
  admin_notes?: string;
}
interface BookingDetail extends BookingListItem {
  updated_at: string;
  cancellation_reason?: string;
  // FEAT-030 removal record (null unless status is "removed"). Admin-only.
  removed_by_user_id?: number | null;
  removed_by_name?: string | null;
  removed_by_role?: 'trainer' | 'admin' | null;
  removed_at?: string | null;
  removal_reason?: string | null;
}

const money = (v?: number | null) => {
  if (v === undefined || v === null) return 'N/A';
  const p = typeof v === 'number' ? v : parseFloat(v as any) || 0;
  return `$${p.toFixed(2)}`;
};
const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';

/* ---- List ---- */
const BOOKING_TABS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'removed', label: 'Removed' },
];
const BOOKING_SORT = [
  { field: 'created_at', label: 'Booked on' },
  { field: 'status', label: 'Status' },
  { field: 'price_paid', label: 'Amount' },
  { field: 'id', label: 'ID' },
];
const BOOKING_SORT_LABELS = Object.fromEntries(BOOKING_SORT.map((f) => [f.field, f.label]));

const BookingStatusTabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.status as string) || 'all';
  return (
    <SegmentedTabs
      tabs={BOOKING_TABS}
      value={current}
      onChange={(v) => setFilters({ ...filterValues, status: v === 'all' ? undefined : v }, displayedFilters)}
    />
  );
};

const BookingStats = () => {
  const base = { pagination: { page: 1, perPage: 1 }, sort: { field: 'created_at', order: 'DESC' } } as const;
  const confirmed = useGetList('bookings', { ...base, filter: { status: 'confirmed' } });
  const pending = useGetList('bookings', { ...base, filter: { status: 'pending' } });
  const removed = useGetList('bookings', { ...base, filter: { status: 'removed' } });
  const all = useGetList('bookings', { ...base, filter: {} });
  const n = (v?: number) => (v === undefined ? '—' : v);
  return (
    <StatRow>
      <StatCard primary label="Confirmed" value={n(confirmed.total)} />
      <StatCard label="Pending" value={n(pending.total)} />
      <StatCard label="Removed" value={n(removed.total)} />
      <StatCard label="Total bookings" value={n(all.total)} />
    </StatRow>
  );
};

const BookingsListBody = () => {
  const columns: DataColumn<BookingListItem>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    {
      key: 'athlete',
      header: 'Athlete',
      cell: (r) => (r.user ? <AvatarName name={r.user.name} subtext={r.user.email} /> : <span className="text-ink-3">No user</span>),
    },
    {
      key: 'session',
      header: 'Session',
      cell: (r) =>
        r.session ? (
          <div className="min-w-0">
            <div className="truncate font-medium text-ink">{r.session.title || r.session.session_type || 'Session'}</div>
            <div className="text-[13px] text-ink-2">{fmtDate(r.session.scheduled_at)}</div>
          </div>
        ) : (
          <span className="text-ink-3">—</span>
        ),
    },
    { key: 'trainer', header: 'Trainer', cell: (r) => (r.trainer ? <span className="text-ink">{r.trainer.name}</span> : <span className="text-ink-3">—</span>) },
    { key: 'sport', header: 'Sport', cell: (r) => <SportTag name={r.sport?.name} /> },
    { key: 'when', header: 'Booked', sortable: true, sortField: 'created_at', cell: (r) => fmtDate(r.created_at) },
    { key: 'price_paid', header: 'Amount', sortable: true, align: 'right', cell: (r) => <span className="tabular-nums">{money(r.price_paid)}</span> },
    { key: 'status', header: 'Status', sortable: true, cell: (r) => <StatusBadge status={r.status} /> },
  ];
  return (
    <ListScreen
      stats={<BookingStats />}
      tabs={<BookingStatusTabs />}
      actions={<SortMenu fields={BOOKING_SORT} />}
      tokens={<FilterTokens fields={[]} sortLabels={BOOKING_SORT_LABELS} />}
    >
      <DataTable columns={columns} rowClick="show" emptyMessage="No bookings found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

export const BookingList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'created_at', order: 'DESC' }} perPage={25} component="div">
    <SearchIntegration filterKey="q" />
    <BookingsListBody />
  </List>
);

/* ---- Update status dialog (kept; needs a select) ---- */
const UpdateBookingStatusDialog = ({
  bookingId,
  currentStatus,
  onClose,
  onSuccess,
}: {
  bookingId: number;
  currentStatus: string;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [status, setStatus] = React.useState(currentStatus);
  const [adminNotes, setAdminNotes] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const dataProvider = useDataProvider<CustomDataProvider>();

  const handleUpdate = async () => {
    setIsSubmitting(true);
    try {
      await dataProvider.updateBookingStatus({ id: bookingId, status, admin_notes: adminNotes });
      toastSuccess('Booking status updated');
      onSuccess();
      onClose();
    } catch (e: any) {
      toastError(e?.message || 'Failed to update booking status');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update booking status</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-4">
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="removed" disabled>Removed (use "Remove from class")</MenuItem>
            </Select>
          </FormControl>
          {currentStatus === 'removed' && (
            <p className="text-[13px] text-ink-2">
              Changing this status lifts the removal — the player will be able to rejoin.
            </p>
          )}
          <MuiTextField
            fullWidth
            multiline
            rows={3}
            label="Admin notes (optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outline" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          className="bg-black text-white hover:bg-ink"
          disabled={isSubmitting || status === currentStatus}
        >
          {isSubmitting ? 'Updating…' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---- Detail ---- */
function DetailCard({ title, icon: Icon, children, className }: { title?: string; icon?: any; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-ink">
          {Icon && <Icon className="size-4 text-ink-2" />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

// Booked → Confirmed → Cancelled / Removed / Declined (terminal)
function StatusTimeline({ status }: { status: BookingDetail['status'] }) {
  const terminal = status === 'cancelled' || status === 'removed' || status === 'declined';
  const order = ['pending', 'confirmed'];
  const currentIdx = order.indexOf(status);
  const steps = [
    { key: 'pending', label: 'Booked' },
    { key: 'confirmed', label: 'Confirmed' },
    {
      key: terminal ? status : 'attended',
      label:
        status === 'removed'
          ? 'Removed'
          : status === 'cancelled'
            ? 'Cancelled'
            : status === 'declined'
              ? 'Declined'
              : 'Attended',
    },
  ];
  return (
    <div className="flex items-center">
      {steps.map((step, i) => {
        const done = !terminal && currentIdx >= i;
        const isCancel = terminal && i === steps.length - 1;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center gap-1.5">
              <span
                className={cn(
                  'flex size-7 items-center justify-center rounded-pill',
                  isCancel ? 'bg-error text-white' : done ? 'bg-indigo text-white' : 'bg-surface-2 text-ink-3',
                )}
              >
                {isCancel ? <XIcon className="size-4" /> : done ? <Check className="size-4" /> : <Circle className="size-3" />}
              </span>
              <span className={cn('text-[12px] font-medium', isCancel ? 'text-error' : done ? 'text-ink' : 'text-ink-3')}>
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={cn('mx-2 h-0.5 flex-1', !terminal && currentIdx > i ? 'bg-indigo' : 'bg-hairline')} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const BookingShowContent = () => {
  const { record, isLoading } = useShowContext<BookingDetail>();
  const refresh = useRefresh();
  const notify = useNotify();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [updateOpen, setUpdateOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const { setData } = useBreadcrumbContext();

  React.useEffect(() => {
    if (record) {
      setData({
        resourceId: record.id.toString(),
        resourceName: `Booking #${record.id}${record.user ? ` · ${record.user.name}` : ''}`,
        resourceType: 'booking',
      });
    }
  }, [record, setData]);

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading booking…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Booking not found</div>;

  const handleDelete = async () => {
    try {
      await dataProvider.deleteBooking({ id: record.id });
      toastSuccess('Booking deleted');
      window.location.href = '/#/bookings';
    } catch {
      toastError('Failed to delete booking');
    }
    void notify;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Record header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
              Booking #{record.id}
            </h1>
            <StatusBadge status={record.status} />
          </div>
          <p className="mt-1 text-sm text-ink-2">
            {record.user?.name}
            {record.session?.title && ` · ${record.session.title}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canRemoveFromClass(record.session?.session_type, record.status) && (
            <RemoveFromClassAction
              bookingId={record.id}
              playerName={record.user?.name}
              sessionId={record.session?.id}
              variant="button"
              onRemoved={refresh}
            />
          )}
          <button
            onClick={() => setUpdateOpen(true)}
            className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2"
          >
            <Edit className="size-4" />
            Update status
          </button>
          <button
            onClick={() => setDeleteOpen(true)}
            className="inline-flex h-9 items-center gap-2 rounded-pill bg-error px-4 text-sm font-semibold text-white hover:bg-error/90"
          >
            <Trash2 className="size-4" />
            Delete
          </button>
        </div>
      </div>

      {record.status === 'cancelled' && record.cancellation_reason && (
        <div className="rounded-card border border-error/30 bg-error-bg px-4 py-3 text-sm text-error">
          <strong>Cancelled:</strong> {record.cancellation_reason}
        </div>
      )}

      {record.status === 'removed' && (
        <div className="rounded-card border border-error/30 bg-error-bg px-4 py-3 text-sm text-error">
          <strong>Removed</strong> by {record.removed_by_name || 'unknown'}
          {record.removed_by_role && ` (${record.removed_by_role === 'trainer' ? 'Trainer' : 'Admin'})`}
          {record.removed_at && ` · ${new Date(record.removed_at).toLocaleString()}`}
          {` · Reason: ${record.removal_reason || '—'}`}
        </div>
      )}

      <DetailCard title="Status">
        <StatusTimeline status={record.status} />
      </DetailCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <DetailCard title="User" icon={User}>
            {record.user ? (
              <div className="space-y-3">
                <Link to={`/users/${record.user.id}/show`} className="font-semibold text-indigo hover:text-indigo-press">
                  {record.user.name}
                </Link>
                {record.user.email && (
                  <div className="flex items-center gap-2 text-sm text-ink"><Mail className="size-4 text-ink-2" />{record.user.email}</div>
                )}
                {record.user.phone && (
                  <div className="flex items-center gap-2 text-sm text-ink"><Phone className="size-4 text-ink-2" />{record.user.phone}</div>
                )}
              </div>
            ) : (
              <p className="text-sm text-ink-2">No user information</p>
            )}
          </DetailCard>

          <DetailCard title="Session" icon={Calendar}>
            {record.session ? (
              <div className="divide-y divide-hairline">
                <Fact
                  label="Session"
                  value={
                    <Link to={`/sessions/${record.session.id}/show`} className="text-indigo hover:text-indigo-press">
                      {record.session.title || record.session.session_type || 'Session'}
                    </Link>
                  }
                />
                {record.session.session_type && <Fact label="Type" value={record.session.session_type} />}
                {record.session.scheduled_at && <Fact label="Scheduled" value={new Date(record.session.scheduled_at).toLocaleString()} />}
                {record.sport && <Fact label="Sport" value={<SportTag name={record.sport.name} />} />}
                {record.trainer && (
                  <Fact
                    label="Trainer"
                    value={
                      <Link to={`/trainers/${record.trainer.id}/show`} className="text-indigo hover:text-indigo-press">
                        {record.trainer.name}
                      </Link>
                    }
                  />
                )}
              </div>
            ) : (
              <p className="text-sm text-ink-2">No session information</p>
            )}
          </DetailCard>
        </div>

        <div className="space-y-6">
          <DetailCard title="Booking">
            <div className="divide-y divide-hairline">
              <Fact label="Booking ID" value={`#${record.id}`} />
              <Fact label="Status" value={<StatusBadge status={record.status} />} />
              <Fact label="Created" value={new Date(record.created_at).toLocaleString()} />
              {record.updated_at && <Fact label="Updated" value={new Date(record.updated_at).toLocaleString()} />}
            </div>
          </DetailCard>

          {record.price_paid !== undefined && record.price_paid !== null && (
            <DetailCard title="Payment" icon={DollarSign}>
              <div className="text-2xl font-bold text-success">{money(record.price_paid)}</div>
            </DetailCard>
          )}

          {record.admin_notes && (
            <DetailCard title="Admin notes">
              <p className="text-sm text-ink">{record.admin_notes}</p>
            </DetailCard>
          )}
        </div>
      </div>

      {updateOpen && (
        <UpdateBookingStatusDialog
          bookingId={record.id}
          currentStatus={record.status}
          onClose={() => setUpdateOpen(false)}
          onSuccess={refresh}
        />
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        tone="destructive"
        title={`Delete booking #${record.id}?`}
        consequence={`This permanently deletes the booking and updates the session participant count. Cannot be undone.${
          record.status === 'removed'
            ? ' This also lifts the removal — the player will be able to rejoin this class.'
            : ''
        }`}
        confirmLabel="Delete booking"
        onConfirm={handleDelete}
      />
    </div>
  );
};

export const BookingShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <BookingShowContent />
  </Show>
);
